import { usePharmacistEscriptContext } from '@/context/escript/EscriptProvider';
import type { PharmacyReferralDto } from '@montugroup/circuit-api-contracts';
import { Box } from '@montugroup/design-system';
import { CursorTable } from '@montugroup/frontend-common';
import { debounce } from '@mui/material';
import type { ColumnDef } from '@tanstack/react-table';
import { useLayoutEffect, useRef, useState } from 'react';

const MIN_TABLE_HEIGHT = 400;
const FOOTER_HEIGHT = 155;

const columns: ColumnDef<PharmacyReferralDto>[] = [
  {
    accessorKey: 'id',
    header: 'ID',
    enableSorting: false,
    cell: ({ row }) => <Box>{row.original.id}</Box>,
  },
  {
    accessorKey: 'token',
    header: 'Token',
    enableSorting: false,
    cell: ({ row }) => <Box>{row.original.token}</Box>,
  },
  {
    accessorKey: 'submissionDate',
    header: 'Submission Date',
    enableSorting: false,
    cell: ({ row }) => <Box>{new Date(row.original.submissionDate).toLocaleDateString()}</Box>,
  },
];

function EscriptTable() {
  const { escripts, loading, controller } = usePharmacistEscriptContext();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerHeight, setContainerHeight] = useState(MIN_TABLE_HEIGHT);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const viewportHeight = window.innerHeight;
        const availableHeight = viewportHeight - containerTop - FOOTER_HEIGHT;
        setContainerHeight(Math.max(availableHeight, MIN_TABLE_HEIGHT));
      }
    };

    handleResize();
    const debounced = debounce(handleResize, 100);
    window.addEventListener('resize', debounced);
    return () => window.removeEventListener('resize', debounced);
  }, []);

  return (
    <Box ref={containerRef}>
      <CursorTable
        data={escripts}
        columns={columns}
        isLoading={loading}
        controller={controller!}
        maxHeight={containerHeight}
      />
    </Box>
  );
}

export default EscriptTable;
